import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Shield } from "lucide-react";

/**
 * BLOC 19/10 — Signup fermé (P0 LLDLS).
 * - Pré-remplissage code depuis `?invite=WERKO-XXXX-XXXX`
 * - Vérification serveur-side à la saisie du code (debounced) → pré-remplit
 *   nom entreprise si fourni.
 * - Soumission : backend valide atomiquement, signup refusé si code invalide
 *   en prod (feature flag `SIGNUP_INVITE_REQUIRED`).
 */
export default function SignupPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    company_name: "",
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    invite_code: params.get("invite") || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyState, setVerifyState] = useState({ checked: false, valid: false, reason: null });
  const { signup } = useAuth();
  const nav = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Vérification serveur quand le code est tapé (debounced simple).
  useEffect(() => {
    const code = form.invite_code.trim().toUpperCase();
    if (code.length < 14) {
      setVerifyState({ checked: false, valid: false, reason: null });
      return;
    }
    setVerifying(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/signup-invites/verify", { code });
        setVerifyState({ checked: true, valid: data.valid, reason: data.reason || null });
        if (data.valid && data.intended_company_name && !form.company_name) {
          setForm((f) => ({ ...f, company_name: data.intended_company_name }));
        }
      } catch (e) {
        setVerifyState({ checked: true, valid: false, reason: "error" });
      } finally {
        setVerifying(false);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.invite_code]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signup({
        ...form,
        email: form.email.trim().toLowerCase(),
        company_name: form.company_name.trim(),
        invite_code: form.invite_code.trim().toUpperCase() || undefined,
      });
      toast.success("Entreprise créée", { description: "Bienvenue !" });
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reasonLabel = {
    not_found: "Code introuvable",
    expired: "Code expiré",
    used: "Code déjà utilisé",
    revoked: "Code révoqué",
    error: "Erreur de vérification",
  };

  return (
    <AuthShell
      title="Créer mon entreprise"
      subtitle="Accès réservé aux prospects munis d'un code d'invitation Wérko."
      footer={
        <>
          Déjà un compte ?{" "}
          <Link to="/login" className="font-medium text-[#C4553B] hover:underline" data-testid="link-login">
            Se connecter
          </Link>
          <br />
          Pas de code ?{" "}
          <Link to="/access-request" className="font-medium text-[#C4553B] hover:underline">
            Demander un accès
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
        <div className="space-y-2">
          <Label className="text-slate-300 flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-[#C4553B]" />
            Code d&apos;invitation
          </Label>
          <div className="relative">
            <Input
              required
              value={form.invite_code}
              onChange={(e) => setForm({ ...form, invite_code: e.target.value.toUpperCase() })}
              className="border-slate-700 bg-slate-950/40 text-slate-100 font-mono uppercase tracking-wider"
              placeholder="WERKO-XXXX-XXXX"
              data-testid="signup-invite-input"
              autoComplete="off"
            />
            {verifying && (
              <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-slate-400" />
            )}
            {!verifying && verifyState.checked && verifyState.valid && (
              <CheckCircle2
                className="absolute right-3 top-2.5 h-4 w-4 text-emerald-400"
                data-testid="signup-invite-valid"
              />
            )}
            {!verifying && verifyState.checked && !verifyState.valid && (
              <XCircle
                className="absolute right-3 top-2.5 h-4 w-4 text-red-400"
                data-testid="signup-invite-invalid"
              />
            )}
          </div>
          {verifyState.checked && !verifyState.valid && (
            <p className="text-xs text-red-400" data-testid="signup-invite-reason">
              {reasonLabel[verifyState.reason] || "Code non valide"}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Nom de l&apos;entreprise</Label>
          <Input
            required
            value={form.company_name}
            onChange={set("company_name")}
            className="border-slate-700 bg-slate-950/40 text-slate-100"
            placeholder="ToitPro SPRL"
            data-testid="signup-company-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-slate-300">Prénom</Label>
            <Input
              required
              value={form.first_name}
              onChange={set("first_name")}
              className="border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="signup-firstname-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Nom</Label>
            <Input
              required
              value={form.last_name}
              onChange={set("last_name")}
              className="border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="signup-lastname-input"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Email</Label>
          <Input
            required
            type="email"
            value={form.email}
            onChange={set("email")}
            className="border-slate-700 bg-slate-950/40 text-slate-100"
            data-testid="signup-email-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Mot de passe (8 caractères min.)</Label>
          <PasswordInput
            required
            minLength={8}
            value={form.password}
            onChange={set("password")}
            className="border-slate-700 bg-slate-950/40 text-slate-100"
            testIdPrefix="signup-password"
          />
        </div>
        {error && (
          <div className="rounded border border-red-500/30 bg-red-500/10 p-2.5 text-sm text-red-300" data-testid="signup-error">
            {error}
          </div>
        )}
        <Button
          type="submit"
          disabled={loading || (verifyState.checked && !verifyState.valid)}
          className="w-full bg-[#C4553B] text-white hover:bg-[#A8402C] disabled:opacity-50"
          data-testid="signup-submit-btn"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Créer mon compte
        </Button>
      </form>
    </AuthShell>
  );
}
